/**
 * MobileTeamsManagersList
 *
 * Phone replacement for the expandable teams/managers table. The desktop table
 * carries five columns plus an expander and nests a second three-column table
 * inside each expanded row — roughly 900px of content before the nested table,
 * which on a 360px screen is a sideways-scrolling strip.
 *
 * Read-only, presentational: it renders what TeamsManagersTab already loaded.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import ButtonBase from "@mui/material/ButtonBase";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsIcon from "@mui/icons-material/Groups";
import Langs, { Localize } from "../../../app/lang/langs";

function matchesFilter(team, filter) {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    const haystack = [
        team.name,
        team.client_name,
        team.localization,
        ...(team.managers || []).map((m) => `${m.prenom ?? ""} ${m.nom ?? ""} ${m.email ?? ""}`),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(needle);
}

function TeamCard({ team }) {
    const [expanded, setExpanded] = useState(false);
    const managerCount = team.manager_count || 0;
    const hasManagers = !!(team.managers && team.managers.length);

    return (
        <Card
            elevation={0}
            sx={{
                mb: 1.5,
                borderRadius: "16px",
                bgcolor: "rgba(255,255,255,0.94)",
                border: "1px solid rgba(0, 40, 128, 0.08)",
                boxShadow: "0 1px 2px rgba(0, 40, 128, 0.04), 0 4px 12px rgba(0, 40, 128, 0.05)",
            }}
        >
            <ButtonBase
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-label={`${team.name || ""} — ${Localize("MANAGERS")}`}
                sx={{
                    width: "100%",
                    textAlign: "left",
                    display: "block",
                    borderRadius: "16px",
                    minHeight: 56,
                }}
            >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: "rgb(0, 40, 128)",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {team.name}
                            </Typography>
                            {team.client_name && (
                                <Typography sx={{ fontSize: "0.85rem", color: "rgba(0, 40, 128, 0.6)" }}>
                                    {team.client_name}
                                </Typography>
                            )}
                            {team.localization && (
                                <Typography sx={{ fontSize: "0.8rem", color: "rgba(0, 40, 128, 0.45)" }}>
                                    {team.localization}
                                </Typography>
                            )}
                        </Box>
                        <Chip
                            icon={<GroupsIcon sx={{ fontSize: "0.9rem" }} />}
                            label={managerCount}
                            size="small"
                            sx={{
                                height: 28,
                                fontWeight: 700,
                                borderRadius: "8px",
                                flexShrink: 0,
                                bgcolor: managerCount > 0 ? "rgba(58, 156, 96, 0.12)" : "rgba(211, 47, 47, 0.10)",
                                color: managerCount > 0 ? "rgb(40, 120, 74)" : "rgb(176, 32, 32)",
                                "& .MuiChip-icon": { color: "inherit" },
                            }}
                        />
                        <ExpandMoreIcon
                            sx={{
                                flexShrink: 0,
                                color: "rgba(0, 40, 128, 0.45)",
                                transform: expanded ? "rotate(180deg)" : "none",
                                transition: "transform 200ms ease",
                                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                            }}
                        />
                    </Box>
                </CardContent>
            </ButtonBase>

            <Collapse in={expanded} unmountOnExit>
                <Divider sx={{ borderColor: "rgba(0, 40, 128, 0.06)" }} />
                <Box sx={{ px: 2, py: 1.5 }}>
                    {!hasManagers ? (
                        <Typography
                            sx={{ fontSize: "0.85rem", fontStyle: "italic", color: "rgba(0, 40, 128, 0.5)" }}
                        >
                            <Langs str="NO_MANAGERS_ASSIGNED" />
                        </Typography>
                    ) : (
                        team.managers.map((m, idx) => (
                            <Box key={m.id ?? `${m.email}-${idx}`} sx={{ py: 1 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            color: "rgb(0, 40, 128)",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {`${m.prenom ?? ""} ${m.nom ?? ""}`.trim()}
                                    </Typography>
                                    {m.profile_name && (
                                        <Chip
                                            label={m.profile_name}
                                            size="small"
                                            sx={{
                                                height: 24,
                                                fontSize: "0.72rem",
                                                fontWeight: 600,
                                                borderRadius: "8px",
                                                flexShrink: 0,
                                                bgcolor: "rgba(255, 144, 21, 0.12)",
                                                color: "#c96f00",
                                            }}
                                        />
                                    )}
                                </Box>
                                {m.email && (
                                    <Typography
                                        sx={{
                                            fontSize: "0.8rem",
                                            color: "rgba(0, 40, 128, 0.55)",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {m.email}
                                    </Typography>
                                )}
                                {idx < team.managers.length - 1 && (
                                    <Divider sx={{ mt: 1, borderColor: "rgba(0, 40, 128, 0.05)" }} />
                                )}
                            </Box>
                        ))
                    )}
                </Box>
            </Collapse>
        </Card>
    );
}

export default function MobileTeamsManagersList({ teams, filter }) {
    const visible = (teams || []).filter((t) => matchesFilter(t, filter));

    if (!visible.length) {
        return (
            <Box sx={{ py: 4, textAlign: "center", color: "rgba(0, 40, 128, 0.5)", fontSize: "0.9rem" }}>
                <Langs str="NO_RESULTS" />
            </Box>
        );
    }

    return (
        <Box>
            {visible.map((team) => (
                <TeamCard key={team.id} team={team} />
            ))}
        </Box>
    );
}
